import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSortable } from '@dnd-kit/sortable';
import Icon, { COLORS, ICONS } from '../icon';
import { SUPPORTED_CHAT_FILE_FORMATS } from '../../../types/constants';
import GRInput from './gr-input';
import { GreenroomState as GS } from '../../../stores/types/types';
import { UploadDocument } from '../../../connection/upload-document';
import { showAlert } from '../alert-service';
import InputClearButton from './input-clear-button';
import { Field, FieldValue, maxPictureCount } from '../../types/stylepacks';
import { ImageEditorResult } from '../../modal/templates/image-cropper';
import * as ModalActions from '../../../stores/actions/modal';
import { setModalState } from '../../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../../stores/reducers/modal';
import { useMediaDevicesContext } from '../../../contexts/media/check-media';
import InfoBadge, { InfoBadgeMsg } from '../../general-components/tooltips/info-badge';

interface GrPictureInputProps {
	onChange: (event: any) => void;
	addPicture?: (field: Field) => void;
	removePicture?: (field: Field, count: number) => void;
	values?: Record<string, FieldValue>;
	count?: number;
	totalCount?: number;
	defaultValues?: Record<string, string | object> | undefined;
	draggingCount?: number;
	draggingOver?: number;
	field: Field;
	isModal?: boolean;
}

const svgLine = <svg xmlns="http://www.w3.org/2000/svg" width="395" height="42" viewBox="0 0 395 42" fill="none">
	<g filter="url(#filter0_d_25759_94984)">
	<path d="M21 21H374" stroke="#A6FF79" stroke-linecap="round"/>
	</g>
	<defs>
	<filter id="filter0_d_25759_94984" x="0.5" y="0.5" width="394" height="41" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
		<feFlood flood-opacity="0" result="BackgroundImageFix"/>
		<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
		<feOffset/>
		<feGaussianBlur stdDeviation="10"/>
		<feComposite in2="hardAlpha" operator="out"/>
		<feColorMatrix type="matrix" values="0 0 0 0 0.65098 0 0 0 0 1 0 0 0 0 0.47451 0 0 0 1 0"/>
		<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_25759_94984"/>
		<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_25759_94984" result="shape"/>
	</filter>
	</defs>
</svg>

export const getNumberedFieldName = (name: string, count: number) => name.replace('${i}', count.toString());

export const getUserFriendlyPictureName = (possiblePictureName: string | undefined) => {
	if (possiblePictureName === '') return '';
	if (possiblePictureName && !possiblePictureName?.includes('/title_templates')) {
		const parts = possiblePictureName?.split('/');
		if (parts.length > 1) {
			return parts[parts.length - 1]?.substring(11) ?? 'preview.png';
		}
	}
	return 'preview.png';
};

function urlOfShownImage(formValueUrl: FieldValue | undefined, defaultUrl: string | object | undefined): string {
	let returnedUrl = '';
	if (typeof formValueUrl === 'string') returnedUrl = formValueUrl;
	else if (typeof defaultUrl === 'string') returnedUrl = defaultUrl;

	if (process.env.REACT_APP_ASSET_URL && returnedUrl.startsWith('./media/api-brandlive')) {
		// replace the default url slug for capsule with our static assets
		const path = process.env.REACT_APP_ASSET_URL + '/title_templates/stock_images'
		return returnedUrl.replace('./media/api-brandlive', path)
	}

	return returnedUrl;
}

export default function GrPictureInput(props: GrPictureInputProps) {
	const dispatch = useDispatch();
	const {
		onChange,
		addPicture = () => {},
		removePicture = () => {},
		values,
		count = 1,
		totalCount = 1,
		defaultValues,
		draggingCount = 0,
		draggingOver = 0,
		field,
		isModal = false
	} = props;
	
	const {
		userData: fieldName,
		options,
		variables = [],
	} = field;

	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const mediaItems = useSelector((s: GS) => s.MediaReducer.media);

	const [numberedFieldName, backUpFieldName] = React.useMemo(
		() => [getNumberedFieldName(fieldName, count), getNumberedFieldName(fieldName, 2)],
		[fieldName, count]
	)

	const { checkMediaDevices } = useMediaDevicesContext();

	const {
		attributes,
		listeners,
		setNodeRef,
		isDragging,
	} = useSortable({id: numberedFieldName});

	const [pictureName, setPictureName] = React.useState('preview.png');
	const [mediaItem, setMediaItem] = React.useState<any>(null);
	const file = useRef<HTMLInputElement | null>(null);
	const image = useRef<HTMLDivElement | null>(null);
	const token = useSelector((s: GS) => s.SessionReducer.token);

	useEffect(() => {
		if (mediaItem) {
			setPictureName(mediaItem?.original_name);
		} else {
			const value = values?.[numberedFieldName];
			if (typeof value === 'string') {
				setPictureName(getUserFriendlyPictureName(value));
			}
		}
	}, [mediaItem, values]);

	useEffect(() => {
		const currentImageUrl = urlOfShownImage(values?.[numberedFieldName], defaultValues?.[numberedFieldName]);

		if (currentImageUrl) {
			const items = mediaItems.filter(x => {
				if (x.provider == 'removebg') {
					const data = x.media_data as any;
					return data?.source == currentImageUrl || data?.original == currentImageUrl;
				}
				return false;
			});

			if (items && items.length) {
				setMediaItem(items[0]);
			} else {
				setMediaItem(null);
			}
		} else {
			setMediaItem(null);
		}
	}, [values, numberedFieldName, defaultValues, mediaItems]);

	function openPhotoModal() {
		const template = MODAL_TEMPLATES.GR_PICTURE;
		const currentImageUrl = urlOfShownImage(values?.[numberedFieldName], defaultValues?.[numberedFieldName]);

		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: template,
			modalData: {
				mode: 'square',
				onConfirm: handleConfirm,
				onClose: () => dispatch(ModalActions.setModalClosed()),
				allowCamera: true,
				cameraStream: mainVideoStream,
				mirrorCamera: true,
				labels: {
					actionNoun: 'image',
					headerEdit: 'Presenter Image',
					headerUpload: 'Presenter Image',
					headerCamera: 'Presenter Image'
				},
				initialImage: currentImageUrl ? {
					url: currentImageUrl,
					name: pictureName,
					type: 'image/png'
				} : undefined,
				mediaItem,
				onInvalidFileType: () =>
					showAlert({
						message: 'Unsupported file',
						description: 'Please upload a PNG or JPG image.',
						duration: 3000,
					})
			},
		}));
		checkMediaDevices();
	}

	const processUpload = async (files: FileList | File) => {
		try {
			const result = await UploadDocument(token, files);
			if (result) {
				setPictureName(result.originalName);
				if (image.current?.style) {
					image.current.style.backgroundImage = `url(${ result.location })`;
				}
			}
			onChange({
				target: {
					name: numberedFieldName,
					value: result.location,
				},
			});
		} catch (error) {
			showAlert({
				message: 'Upload Error',
				description: 'We ran into an issue uploading that image. Please try again',
				duration: 4000,
			});
			console.warn(error);
		}
	};

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			await processUpload(event.target.files);
		}

		if (file?.current?.value) {
			file.current.value = '';
		}
	};

	async function handleConfirm(data: ImageEditorResult) {
		if (!data.file) {
			return;
		}
		if (data.uploaded) {
			setPictureName(data.uploaded.name);
			onChange({
				target: {
					name: numberedFieldName,
					value: data.uploaded.url,
				},
			});
		} else {
			await processUpload(data.file);
		}
		dispatch(ModalActions.setModalClosed());
	}

	const clearInput = () => {
		setPictureName('')
		onChange({target: {name: numberedFieldName, value: ''}})
	}

	const backgroundImage = useMemo(
		() => `url(${urlOfShownImage(values?.[numberedFieldName], defaultValues?.[backUpFieldName])})`,
		[values?.[numberedFieldName], defaultValues?.[backUpFieldName]]
	);

	const iconToUseForAddButton = defaultValues?.['button-icon'] && typeof defaultValues?.['button-icon'] === 'string'
		? ICONS[defaultValues['button-icon'] as keyof typeof ICONS]
		: undefined;

	const height = useRef(300)

	if (isDragging) {
		return <div className='gr-input-picture' key={`picture-${count}`} id={`picture-${count}`}>
			<div className="drag-holder" style={{height: `${height.current}px`}}/>
		</div>
	}

	return (
		<div
			className={'gr-input-picture styled-input-container'}
			key={`picture-${count}`}
			id={`picture-${count}`}
			ref={(ref) => {
				if (ref) height.current = ref.offsetHeight;
				setNodeRef(ref)
			}}
			{...attributes}
		>
			{!!defaultValues?.oneToManyLabel && (
				<>
					{count > 1 && (
						<hr className="header-line" />
					)}
					{draggingOver === count && draggingCount > count && (
						<div className="dragging-over">
							{svgLine}
						</div>
					)}
					<div className="gr-input-picture-label">
						<div className="gr-input-picture-drag" {...listeners}>
							<div className="gr-input-picture-label-drag">
								<Icon name={ICONS.DRAG} size={12} color={COLORS.WHITE}/>
							</div>
							{!!defaultValues?.oneToManyLabel 
								? `${defaultValues?.oneToManyLabel}` 
								: 'Picture'}&nbsp;{count}
						</div>
						<div
							className="gr-input-picture-delete-button"
							onClick={() => removePicture(field, count)}
						>
							Delete
						</div>
					</div>
				</>
			)}
			<div className="gr-input-picture-fields">
				<input
					type="file"
					style={{ display: 'none' }}
					ref={file}
					onChange={handleUpload}
					accept={SUPPORTED_CHAT_FILE_FORMATS.PICTURE_MIME_TYPES.toString()}
				/>
				{variables.map((variable: any) => {
					const subFieldName = getNumberedFieldName(variable.userData, count);
					const backUpFieldName = getNumberedFieldName(variable.userData, 2);

					let value = values?.[fieldName] ?? '';
					if (
						!value
						&& value !== ''
						&& values?.[subFieldName]
						&& count >= 2
						&& typeof defaultValues?.[backUpFieldName] === 'string'
					) {
						value = defaultValues?.[backUpFieldName] as string;
					}

					return (
						<GRInput
							key={variable.name}
							fieldName={getNumberedFieldName(variable.userData, count)}
							friendlyName={variable.name}
							fieldType={variable.type}
							onChange={onChange}
							options={options}
							value={value ?? ''}
						/>
					);
				})}
			</div>
			<div className="gr-input-picture-row">
				<div
					className="gr-input-picture-thumbnail flex-center"
					style={{backgroundImage}}
					ref={image}
					onClick={() => {isModal ? openPhotoModal() : file?.current?.click()}}
				>
					{pictureName ? null : <Icon name={ICONS.MEDIA_UNFILLED} size={18} color={COLORS.WHITE}/>}
					&nbsp;
				</div>
				<div style={{ flexGrow: 1, display: 'flex' }}>
					<label className="gr-input-picture-image-label">Image</label>
					<InfoBadge
						message={InfoBadgeMsg.StylepackImage}
						customElement={<Icon name={ICONS.INFO_CIRCLE} color={'#9B97A1'} size={12}></Icon>}
					/>
				</div>
				<div
					className="gr-input-picture-button bl-font-l2"
					onClick={() => {isModal ? openPhotoModal() : file?.current?.click()}}
				>
					<Icon size={10} color={COLORS.TEXT_GRAY_200} name={ICONS.EDIT} />
				</div>
				<InputClearButton callback={clearInput} imageField={true} disabled={!pictureName}/>
			</div>
			{draggingOver === count && draggingCount < count && (
				<div className="dragging-over">{svgLine}</div>
			)}
			{defaultValues?.oneToManyField === fieldName
				&& iconToUseForAddButton
				&& count === 1
				&& totalCount < maxPictureCount
				&& (<div className="gr-input-add-button" onClick={() => isModal ? openPhotoModal() : addPicture(field)}>
					<>
						{!!defaultValues['button-icon'] ? (
							<Icon name={iconToUseForAddButton} size={12} color={COLORS.TEXT_GRAY_200}/>
						) : null}
						{typeof defaultValues['button-text'] === 'string' 
							? defaultValues['button-text'] : 'Add'}
					</>
				</div>)
			}
		</div>
	);
}
